import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Stack,
    Grid,
    Chip,
    CircularProgress,
    Divider,
} from "@mui/material";
import QuizIcon from "@mui/icons-material/Quiz";
import DownloadIcon from "@mui/icons-material/Download";
import { useLocation } from "react-router-dom";

import CustomCard from "../../components/common/CustomCard";
import CustomButton from "../../components/common/CustomButton";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomHeader from "../../components/common/CustomHeader";


import { questionApi } from "../../api/questionanswerApi";

const GenerateQuestionsPage = () => {
    const { state } = useLocation();
    const jdId = state?.jdId;

    const [numQuestions, setNumQuestions] = useState(5);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "",
    });

    // 🔹 Generate Questions using centralized API
    const handleGenerate = async () => {
        if (!jdId) {
            setSnackbar({
                open: true,
                message: "No JD ID found. Please navigate from JD list.",
                severity: "warning",
            });
            return;
        }

        try {
            setLoading(true);
            setQuestions([]);

            const response = await questionApi.generateQuestionsFromJD({
                jd_id: jdId,
                num_questions: numQuestions,
            });
            // console.log("Generate questions response:", response);
            const { status, message, data } = response || {};

            /* 1️⃣ Network error */
            if (status === 0) {
                setSnackbar({
                    open: true,
                    message: "🌐 Network error. Please check your internet connection.",
                    severity: "warning",
                });
                return;
            }

            /* 2️⃣ Server error */
            if (status >= 500) {
                setSnackbar({
                    open: true,
                    message:
                        message ||
                        "❌ Server error while generating questions.",
                    severity: "error",
                });
                return;
            }

            /* 3️⃣ Client / validation error */
            if (status >= 400) {
                setSnackbar({
                    open: true,
                    message:
                        message ||
                        "⚠️ Unable to generate questions for this job description.",
                    severity: "warning",
                });
                return;
            }
            if (status === 200 && Array.isArray(data) && data.length > 0) {
                setQuestions(data);
                return;
            }

            /* 5️⃣ Empty but valid response */
            if (status === 200 && Array.isArray(data) && data.length === 0) {
                setSnackbar({
                    open: true,
                    message: "No questions generated. Please try again.",
                    severity: "info",
                });
                return;
            }

            /* 6️⃣ Fallback */
            setSnackbar({
                open: true,
                message: "⚠️ Unexpected response while generating questions.",
                severity: "warning",
            });



        } catch (error) {
            console.error("[GenerateQuestionsPage] Error:", error);
            setSnackbar({
                open: true,
                message:
                    "❌ Something went wrong while generating questions.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Download CSV
    const handleDownload = () => {
        const csvContent =
            "data:text/csv;charset=utf-8," +
            ["Question,Answer,Skill"]
                .concat(
                    questions.map(
                        (q) =>
                            `"${q.question.replace(/"/g, '""')}","${q.answer.replace(
                                /"/g,
                                '""'
                            )}","${q.skill || ""}"`
                    )
                )
                .join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "generated_questions.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
            }}
        >
            {/* HEADER */}
            <Box sx={{ mb: 1 }}>
                <CustomHeader
                    title="Generate Interview Questions"
                // secondaryText={`Review candidates matched to the ${jdTitle}`}
                />
            </Box>


            <Container maxWidth="lg">
                {/* Input & Generate Section */}
                <CustomCard sx={{ mb: 4 }} size={{ height: "20%", width: "100%" }}>
                    <Typography sx={{ mb: 2 }}>
                        Enter the number of questions to generate for this JD.
                    </Typography>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <CustomInput
                            type="number"
                            label="Number of Questions"
                            value={numQuestions}
                            onChange={(e) => setNumQuestions(e.target.value)}
                            onBlur={() => {
                                const value = Number(numQuestions);
                                if (value < 1) setNumQuestions(1);
                                else if (value > 60) setNumQuestions(60);
                            }}
                            inputProps={{ min: 1, max: 10 }}
                            sx={{ width: 900 }}
                        />

                        <CustomButton
                            icon={
                                loading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <QuizIcon />
                                )
                            }
                            onClick={handleGenerate}
                            disabled={loading}
                        >
                            {loading ? "Generating..." : "Generate"}
                        </CustomButton>
                    </Stack>
                </CustomCard>

                {/* Questions Section */}
                {questions.length > 0 && (
                    <CustomCard size={{ height: "100%", width: "100%" }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                        >
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700 }}
                            >
                                Generated Questions ({questions.length})
                            </Typography>

                            <CustomButton
                                icon={<DownloadIcon />}
                                onClick={handleDownload}
                                disabled={questions.length === 0}
                            >
                                Download CSV
                            </CustomButton>
                        </Stack>

                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={3}>
                            {questions.map((q, idx) => (
                                <Grid item xs={12} key={q.id || idx}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            Q{idx + 1}: {q.question}
                                        </Typography>
                                        <Typography sx={{ mt: 0.5 }}>
                                            <b>Ideal Answer:</b> {q.answer}
                                        </Typography>
                                        <Stack direction="row" spacing={1} mt={1}>
                                            <Chip
                                                label={q.skill || "General"}
                                                size="small"
                                            />
                                        </Stack>
                                        {idx !== questions.length - 1 && (
                                            <Divider sx={{ my: 2, borderColor: "#e2e8f0" }} />
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CustomCard>
                )}
            </Container>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={6000}
            />
        </Box>
    );
};

export default GenerateQuestionsPage;
